import { Typography } from '@atlas-metrics/design-system';
import * as RadixTabs from '@radix-ui/react-tabs';
import React from 'react';

import clsxm from '@/lib/clsxm';

import Loader from './Loader';

export interface Tab {
  value: string;
  label: string | JSX.Element;
  loading?: boolean | undefined;
}

export interface IProps {
  tabsList: Tab[];
  value: string;
  onValueChange: (value: string) => void;
  label?: string;
  defaultValue?: string;
  className?: string;
}

const Tabs: React.FC<IProps> = ({
  tabsList,
  value,
  defaultValue,
  onValueChange,
  label,
  className,
}) => {
  const getTabContent = (tab: Tab) => {
    if (typeof tab.label === 'string') {
      return (
        <Typography
          variant='body-m'
          className='w-full whitespace-nowrap text-ds-grey-950'
          bold
        >
          {tab.label}
        </Typography>
      );
    }

    return tab.label;
  };

  return (
    <RadixTabs.Root
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      orientation='vertical'
      className={clsxm('w-full', className)}
    >
      <RadixTabs.List
        aria-label='tabs'
        className='flex w-full items-center gap-2 rounded-2xl bg-ds-grey-50 p-2'
      >
        {label && (
          <Typography variant='body-m' bold className='mx-2 text-ds-grey-700'>
            {label}
          </Typography>
        )}
        {tabsList.map((tab, idx) => (
          <RadixTabs.Trigger
            key={idx}
            value={tab.value}
            className='relative flex grow items-center justify-center rounded-xl bg-ds-grey-50 py-2 transition-all duration-200 hover:bg-ds-grey-50 data-[state=active]:bg-white data-[state=active]:shadow-raised data-[state=active]:hover:shadow-overlay'
          >
            {/* To keep the size of the button, the text is hidden and the loader is placed on top. */}
            <span className={clsxm(tab.loading && 'invisible')}>
              {getTabContent(tab)}
            </span>
            {tab.loading && (
              <span className='absolute'>
                <Loader />
              </span>
            )}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
    </RadixTabs.Root>
  );
};

export default Tabs;
